import React, { ReactElement, useMemo, useState } from 'react';
import { Flex, Text, VStack } from '@chakra-ui/react';
import { UserIcon } from 'assets/icons/v2';
import { useIsB2BTeacherOrAdmin } from 'hooks/utils/useIsB2BTeacherOrAdmin';
import { ClassesAccessDeniedMessage } from 'components/worksheet/dashboard/ClassesAccessDeniedMessage';
import { GroupFilterInput } from './GroupFilterInput';
import { GroupListItem } from './GroupListItem';
import { SectionTitle } from './SectionTitle';

type Student = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

type Group = {
  id: string;
  name: string;
  isConnectedToWorksheet: boolean;
  students?: Student[];
};

type Props = {
  groups: Group[];
  isLoading: boolean;
  expandedGroupIds: Set<string>;
  loadingGroupId: string | null;
  onToggleExpansion: (groupId: string) => void;
  onQrShare: (group: { name: string }) => void;
  onRemove: (groupId: string) => void;
  onAdd: (groupId: string) => void;
};

export const GroupsList = ({
  groups,
  isLoading,
  expandedGroupIds,
  loadingGroupId,
  onToggleExpansion,
  onQrShare,
  onRemove,
  onAdd,
}: Props): ReactElement => {
  const [filterText, setFilterText] = useState('');
  const { isB2BUser, isB2BTeacherOrAdmin, isLoading: isAccessLoading } = useIsB2BTeacherOrAdmin();

  const { sharedClasses, availableClasses } = useMemo(() => {
    const shared = groups.filter((group) => group.isConnectedToWorksheet);
    const available = groups.filter((group) => !group.isConnectedToWorksheet);

    if (!filterText) {
      return { sharedClasses: shared, availableClasses: available };
    }

    const lowerFilter = filterText.toLowerCase();
    return {
      sharedClasses: shared,
      availableClasses: available.filter((group) => group.name.toLowerCase().includes(lowerFilter)),
    };
  }, [groups, filterText]);

  if (!isB2BTeacherOrAdmin && !isAccessLoading) {
    return <ClassesAccessDeniedMessage isB2BUser={isB2BUser} />;
  }

  if (isLoading || isAccessLoading) {
    return (
      <Flex justify="center" alignItems="center" flexDir="column" gap="30px">
        <Text>Lade Kurse...</Text>
      </Flex>
    );
  }

  return (
    <Flex justify="center" alignItems="center" flexDir="column" gap="30px" width="100%">
      <VStack gap="30px" align="stretch" width="100%">
        {sharedClasses.length > 0 && (
          <VStack gap="10px" align="stretch" width="100%">
            <SectionTitle
              title="Kurse mit Zugriff"
              icon={<UserIcon width="13.5px" height="13.5px" color="text.secondary" />}
            />
            <VStack gap={0} align="stretch" width="100%">
              {sharedClasses.map((group) => (
                <GroupListItem
                  key={group.id}
                  group={group}
                  isExpanded={expandedGroupIds.has(group.id)}
                  loadingGroupId={loadingGroupId}
                  onToggleExpansion={onToggleExpansion}
                  onQrShare={onQrShare}
                  onRemove={onRemove}
                  onAdd={onAdd}
                />
              ))}
            </VStack>
          </VStack>
        )}

        {(availableClasses.length > 0 || filterText) && (
          <VStack gap="10px" align="stretch" width="100%">
            <SectionTitle
              title="Kurse"
              icon={<UserIcon width="13.5px" height="13.5px" color="text.secondary" />}
            />
            <GroupFilterInput
              value={filterText}
              onChange={setFilterText}
              placeholder="Kurs suchen"
            />
            <VStack gap={0} align="stretch" width="100%">
              {availableClasses.map((group) => (
                <GroupListItem
                  key={group.id}
                  group={group}
                  isExpanded={expandedGroupIds.has(group.id)}
                  loadingGroupId={loadingGroupId}
                  onToggleExpansion={onToggleExpansion}
                  onQrShare={onQrShare}
                  onRemove={onRemove}
                  onAdd={onAdd}
                />
              ))}
            </VStack>
          </VStack>
        )}

        {groups.length === 0 && (
          <Text textAlign="center" color="text.tertiary">
            Keine Kurse verfügbar
          </Text>
        )}
      </VStack>
    </Flex>
  );
};
